import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpTrayIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import * as mcquizApi from '../services/mcquizApi';

export default function McQuizBankUpload() {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['mcquiz-banks'],
    queryFn: () => mcquizApi.listBanks().then((r) => r.data),
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('No file selected');
      if (!title.trim()) throw new Error('Title is required');
      return mcquizApi.uploadQuestionBank(file, title.trim(), description.trim() || undefined);
    },
    onSuccess: () => {
      toast.success('Question bank uploaded');
      setTitle('');
      setDescription('');
      setFile(null);
      queryClient.invalidateQueries({ queryKey: ['mcquiz-banks'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || err?.message || 'Upload failed');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (bankId: number) => mcquizApi.deleteBank(bankId),
    onSuccess: () => {
      toast.success('Bank deleted');
      queryClient.invalidateQueries({ queryKey: ['mcquiz-banks'] });
    },
    onError: () => toast.error('Failed to delete bank'),
  });

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.json')) {
      setFile(droppedFile);
    } else {
      toast.error('Please drop a JSON file');
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleDelete = (bankId: number, bankTitle: string) => {
    if (window.confirm(`Delete bank "${bankTitle}"? All linked questions will be removed.`)) {
      deleteMutation.mutate(bankId);
    }
  };

  const banks: any[] = data?.banks ?? data ?? [];

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Question Banks</h1>

      {/* Upload form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Upload New Bank</h2>

        {/* Dropzone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            dragOver
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
          onClick={() => document.getElementById('bank-file-input')?.click()}
        >
          <ArrowUpTrayIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          {file ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">{file.name}</p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Drop a JSON file here or click to browse
            </p>
          )}
          <input
            id="bank-file-input"
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Title & Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. UPSC Prelims 2025 - Polity"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={1}
              placeholder="Optional description"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm resize-none"
            />
          </div>
        </div>

        <button
          onClick={() => uploadMutation.mutate()}
          disabled={!file || !title.trim() || uploadMutation.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploadMutation.isPending ? (
            <ArrowPathIcon className="w-4 h-4 animate-spin" />
          ) : (
            <ArrowUpTrayIcon className="w-4 h-4" />
          )}
          Upload Bank
        </button>
      </div>

      {/* Bank list */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Existing Banks</h2>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : banks.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">No question banks uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {banks.map((bank: any) => (
              <div
                key={bank.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 flex flex-col gap-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                      {bank.title}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      bank.status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {bank.status || 'active'}
                  </span>
                </div>

                {bank.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {bank.description}
                  </p>
                )}

                <div className="flex flex-wrap gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                    {bank.question_count ?? 0} questions
                  </span>
                  {bank.subjects?.map((s: string) => (
                    <span
                      key={s}
                      className="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                <div className="mt-auto pt-2 flex justify-end">
                  <button
                    onClick={() => handleDelete(bank.id, bank.title)}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Delete bank"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
